import { ActionTree } from 'vuex'
import { Auth } from './types'
import { RootState } from '../types'
import {
  googleAnalytics,
  magentaOAuth,
  connectionService,
  PERSON_FETCH_URI,
  ACCOUNT_FETCH_URI,
  TERMS_CONDITIONS_FETCH_URI,
  TERMS_CONDITIONS_FETCH_LAST_VERSION_URI,
  TERMS_CONDITIONS_ACCEPT_URI,
  RACE_TIMEOUT,
  wampUtils,
  ConnectionWSShared
} from '@leapsight/styleguide'
import store from '@/store'
import { AuthHelpers, GlobalHelpers } from '@/helpers/mainExports'
import router from '@/router'
import i18n from '@/plugins/i18n'

export const actions: ActionTree<Auth, RootState> = {
  getConnection({ commit }, { fnConnOnOpen, fnConnOnClose }) {
    const conn = ConnectionWSShared.getInstance({
      fnConnOnOpen,
      fnConnOnClose
    }).connection
    commit('setConnection', conn)
  },
  establishConnection({ state, commit, dispatch }) {
    return new Promise((resolve, reject) => {
      const fnConnOnClose = async (wsReason, wampDetails) => {
        console.error('Reason disconnect: ', wsReason, wampDetails)
        if (wsReason === 'unreachable' || wsReason === 'unsupported') {
          if (!wampDetails.retry_count || wampDetails.retry_count > 10) {
            const snackbar = {
              show: true,
              color: 'warning',
              text: i18n.t(
                'portal.general.user_settings.user_profile.errors.connection_lost_message'
              )
            }
            store.commit('ui/setSnackbar', snackbar)
            localStorage.clear()
            window.location.reload()
          }
          return false
        }
        const { reason, details } = wampDetails

        if (wampUtils.isReasonToDisconnect({ reason })) {
          wampUtils.TokenServices.clearToken()
          store.commit(
            'ui/setDisconnectDialogMessage',
            i18n.t('general.disconnectDialogComponent.message_modified_profile')
          )
          store.commit('ui/setDisconnectDialogValue', true)
          setTimeout(() => {
            commit('logout')
            localStorage.clear()
            router.push({ name: 'login' }).then(() => {
              window.location.reload()
            })
          }, 10000)
          return true
        }

        if (
          reason === 'wamp.error.authentication_failed' ||
          reason === 'wamp.close.goodbye_and_out'
        ) {
          let willRetry = true
          for (let i = 0; i < 3 && willRetry; i++) {
            try {
              const res = await magentaOAuth.refreshToken()
              if (res.status === 200) {
                dispatch('establishConnection')
                willRetry = false
              } else if (res.status >= 400 && res.status < 500) {
                commit('logout')
                localStorage.clear()
                router.push({ name: 'login' }).then(() => {
                  window.location.reload()
                })
                willRetry = false
              } else {
                willRetry = true
              }
            } catch (err) {
              console.error(err)
              commit('logout')
              localStorage.clear()
              router.push({ name: 'login' }).then(() => {
                window.location.reload()
              })
              willRetry = false
            }
          }
        }
      }

      const fnConnOnOpen = session => {
        const kwArgs = connectionService.createkwArgs()
        const userId = connectionService.getBusinessUserId()
        const accountId = connectionService.getAccountId()
        commit('setSession', session)
        session
          .call(PERSON_FETCH_URI, [userId], kwArgs, {})
          .then(res => {
            const userGroups = res.kwargs.security.groups
            let userIsAdmin
            if (userGroups.includes('business_account_admin')) {
              userIsAdmin = true
              commit('userIsAdmin', true)
            } else {
              userIsAdmin = false
            }

            const accountData = {
              meta: kwArgs.security.meta,
              version: res.args[0].version
            }

            commit('accountData', accountData)

            AuthHelpers.setUserLocalStorage(res)
            commit('setUser', res.args[0])
            commit('wsConnectionActive', true)

            dispatch('subscribeUserUpdated', () => {
              wampUtils.TokenServices.clearToken()
              store.commit(
                'ui/setDisconnectDialogMessage',
                i18n.t(
                  'general.disconnectDialogComponent.message_modified_profile'
                )
              )
              store.commit('ui/setDisconnectDialogValue', true)
              setTimeout(() => store.dispatch('auth/logout'), 10000)
            })

            resolve(userIsAdmin)
          })
          .catch(err => {
            reject(err)
          })
        session
          .call(ACCOUNT_FETCH_URI, [accountId], kwArgs, {})
          .then(data => {
            //Guardo en localStorage el ID del country para que el Footer lo tome y muestre el link correcto a Terminos y Condiciones

            const countryId = data.args[0].country_id.toLowerCase()
            localStorage.setItem('country_id', countryId)

            //Guardo en LocalStorage la zona horario de la cuenta para poder acceder desde day.js en las demas apps
            const timeZoneName = GlobalHelpers.getTimeZoneName(countryId)
            localStorage.setItem('timeZone', timeZoneName)

            //Se almacena en el state información de la cuenta.
            commit('setAccountInformation', data.args[0])
          })
          .catch(err => {
            reject(err)
          })
      }

      if (state.connection?.session) fnConnOnOpen(state.connection.session)
      else dispatch('getConnection', { fnConnOnOpen, fnConnOnClose })
    })
  },

  login({ commit }, user) {
    return new Promise((resolve, reject) => {
      commit('auth_request')
      magentaOAuth
        .login(user.email, user.password)
        .then(resp => {
          switch (resp.status) {
            case 200:
              googleAnalytics()
              commit('auth_success', resp.data.access_token)
              resolve(resp)
              break
            case 400:
              commit('auth_error')
              localStorage.clear()
              reject(resp)
              break
            default:
              break
          }
        })
        .catch(err => {
          commit('auth_error')
          reject(err)
        })
    })
  },
  async logout({ getters, commit }) {
    try {
      const response = await magentaOAuth.logout()
      if (response.status === 200) {
        // Este condicional procede a cerrar la conexión ws si nos encontramos en la app Portal
        // ya que las otras aplicaciones no comparten el state y la cierran de otra manera
        const connection = getters.connection
        if (connection) ConnectionWSShared.closeConnection()
        if (router.currentRoute.name !== 'login') {
          commit('logout')
          router.push({ name: 'login' }).then(() => {
            window.location.reload()
          })
        }
      }
    } catch (e) {
      console.log(e)
    } finally {
      store.commit('ui/setDisconnectDialogValue', false)
    }
  },
  checkTokenIsActive({ commit }) {
    return new Promise((resolve, reject) => {
      //Existe token?
      const token = localStorage.getItem('ACCESS_TOKEN')

      //Valido que este activo
      if (token) {
        magentaOAuth
          .active()
          .then(response => {
            commit('tokenValid', response)
            resolve(response)
          })
          .catch(err => {
            console.log('error ', err)
            reject()
          })
      } else {
        resolve(false)
      }
    })
  },
  accountCheckVersion({ commit, getters }) {
    return new Promise((resolve, reject) => {
      const connection = getters.connection
      const kwArgs = connectionService.createkwArgs()
      const accountId = getters.businessAccountId

      let accountVersionChecked
      connection.session
        .call(ACCOUNT_FETCH_URI, [accountId], kwArgs, {})
        .then(
          res => {
            // Se obtienen todas las versiones aceptadas de la cuenta
            if (res.args[0].hasOwnProperty('terms_and_conditions')) {
              accountVersionChecked = Object.keys(
                res.args[0].terms_and_conditions
              )
              commit('accountVersionAccepted', accountVersionChecked)
              resolve(accountVersionChecked)
            } else {
              accountVersionChecked = []
              commit('accountVersionAccepted', accountVersionChecked)
              resolve(accountVersionChecked)
            }
          },
          wampError => {
            reject(wampError)
          }
        )
        .catch(err => {
          console.log('error ', err)
        })
    })
  },

  getLastVersionTermsCondition({ state, commit }) {
    return new Promise((resolve, reject) => {
      const connection = state.connection
      const countryId = state.accountInformation.country_id
      const kwArgs = connectionService.createkwArgs()
      const p1 = new Promise(resolve => {
        setTimeout(reject, RACE_TIMEOUT, false)
      })
      const p2 = new Promise((resolve, reject) => {
        connection.session
          .call(
            TERMS_CONDITIONS_FETCH_LAST_VERSION_URI,
            [countryId],
            kwArgs,
            {}
          )
          .then(
            res => {
              //Obtiene ultima version y la guarda en state
              const latestVersionTermsConditions = res.version

              commit(
                'latestVersionTermsConditions',
                latestVersionTermsConditions
              )

              resolve(latestVersionTermsConditions)
            },
            wampError => {
              reject(wampError)
            }
          )
          .catch(err => {
            console.log('error ', err)
          })
      })

      Promise.race([p1, p2])
        .then(value => {
          resolve(value)
        })
        .catch(err => {
          reject(err)
        })
    })
  },

  checkVersionTermsConditions({ state, commit }) {
    return new Promise((resolve, reject) => {
      const connection = state.connection
      const lastVersionTermsConditions = state.latestVersionTermsConditions
      const kwArgs = connectionService.createkwArgs()
      const p1 = new Promise(resolve => {
        setTimeout(resolve, RACE_TIMEOUT, false)
      })
      const p2 = new Promise((resolve, reject) => {
        connection.session
          .call(
            TERMS_CONDITIONS_FETCH_URI,
            [lastVersionTermsConditions],
            kwArgs,
            {}
          )
          .then(
            res => {
              const docs = res.docs
              let html = ''
              docs.map(doc => {
                html += doc.html
              })

              // De necesitar aceptar nuevos terminos se bloquea el acceso a la cuenta
              commit('termsConditionsBlocker', true)
              resolve(html)
            },
            wampError => {
              reject(wampError)
            }
          )
          .catch(err => {
            console.log('error ', err)
          })
      })

      Promise.race([p1, p2])
        .then(value => {
          resolve(value)
        })
        .catch(err => {
          reject(err)
        })
    })
  },

  versionAcceptTermsConditions({ state, commit }) {
    return new Promise((resolve, reject) => {
      const connection = state.connection
      const kwArgs = connectionService.createkwArgs()
      const version = state.latestVersionTermsConditions

      connection.session
        .call(TERMS_CONDITIONS_ACCEPT_URI, [version], kwArgs, {})
        .then(res => {
          // Permite avanzar en el flujo del login hacia la ruta siguiente.
          commit('termsConditionsBlocker', false)
          resolve(res)
        })
        .catch(err => {
          reject()
        })
    })
  },
  accountInformationFetch({ commit }) {
    return new Promise((resolve, reject) => {
      const connection = store.getters['auth/connection']
      const kwArgs = connectionService.createkwArgs()
      const accountId = connectionService.getAccountId()
      connection.session
        .call(ACCOUNT_FETCH_URI, [accountId], kwArgs, {})
        .then(data => {
          commit('setAccountInformation', data.args[0])
          resolve(data.args[0])
        })
        .catch(err => {
          console.log(err, 'err')
        })
    })
  },
  subscribeUserUpdated({ getters }, callback) {
    const connection = store.getters['auth/connection']
    const accountId = wampUtils.getAccountUUID(getters.businessAccountId)
    const personId = wampUtils.getPersonUUID(getters.user.id)
    return new Promise<void>(resolve => {
      const topic = `com.magenta.account.${accountId}.user.${personId}.updated`
      connection.session
        .subscribe(topic, callback, { match: 'exact' })
        .then(() => {
          resolve()
        })
    })
  }
}
